import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Container, Offcanvas } from 'react-bootstrap';
import { NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Logo from '../../../assests/logo.png';
import './Navbar.css';
import SearchModal from '../modalComponent/SearchModal/SearchModal';
import { fetchCategories, fetchSubCategories, fetchCollections } from '../../../Services/CategoryServices';
import { selectIsAuthenticated } from '../../../slices/authSlice'; // Add this import

const NavbarComponent = () => {
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [showCollectionsDropdown, setShowCollectionsDropdown] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  const categoryTimeoutRef = useRef(null);
  const collectionTimeoutRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const collectionDropdownRef = useRef(null);

  const wishlistCount = useSelector((state) => state.wishlist.wishlistSlugs.length);
  const cartCount = useSelector((state) => state.cart.items.length);
  const isAuthenticated = useSelector(selectIsAuthenticated); // Add this selector
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return () => {
      if (categoryTimeoutRef.current) clearTimeout(categoryTimeoutRef.current);
      if (collectionTimeoutRef.current) clearTimeout(collectionTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const getCategoriesAndCollections = async () => {
      try {
        const allCategories = await fetchCategories();
        const allSubCategories = await fetchSubCategories();
        
        // Filter active categories (Status === "1")
        const activeCategories = allCategories.filter(category => category.Status === "1");
        
        // Filter active subcategories (Status === "1")
        const activeSubCategories = allSubCategories.filter(subCategory => subCategory.Status === "1");
        
        const categoriesWithSubCategories = activeCategories.map((category) => ({
          ...category,
          subCategories: activeSubCategories.filter(
            (subCategory) => subCategory.CategoryId === category.CategoryId
          ),
        }));
        
        setCategories(categoriesWithSubCategories);
        
        // Filter active collections (Status === "1")
        const allCollections = await fetchCollections();
        const activeCollections = allCollections.filter(collection => collection.Status === "1");
        setCollections(activeCollections);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    getCategoriesAndCollections();
  }, []);

  const handleSearchModalOpen = () => setShowSearchModal(true);
  const handleSearchModalClose = () => setShowSearchModal(false);

  const handleProfileClick = (e) => {
    e.preventDefault();
    // Always redirect to login if not authenticated
    if (!isAuthenticated) {
      // Store the intended destination
      sessionStorage.setItem('redirectAfterLogin', '/profile');
      navigate('/login');
    } else {
      navigate('/profile');
    }
  };

  const handleCategoryClick = (slug) => {
    navigate(`/category/${slug}`);
    setShowOffcanvas(false);
    setShowCategoriesDropdown(false);
  };

  const handleSubCategoryClick = (slug) => {
    navigate(`/subcategory/${slug}`);
    setShowOffcanvas(false);
    setShowCategoriesDropdown(false);
  };

  const handleCollectionClick = (slug) => {
    navigate(`/collection/${slug}`);
    setShowOffcanvas(false);
    setShowCollectionsDropdown(false);
  };

  const handleOffcanvasClose = () => setShowOffcanvas(false);
  const handleOffcanvasShow = () => setShowOffcanvas(true);

  const handleCategoryMouseEnter = () => {
    if (categoryTimeoutRef.current) clearTimeout(categoryTimeoutRef.current);
    setShowCategoriesDropdown(true);
  };

  const handleCategoryMouseLeave = () => {
    categoryTimeoutRef.current = setTimeout(() => {
      if (!categoryDropdownRef.current?.matches(':hover')) {
        setShowCategoriesDropdown(false);
        setHoveredCategory(null);
      }
    }, 300);
  };

  const handleCollectionMouseEnter = () => {
    if (collectionTimeoutRef.current) clearTimeout(collectionTimeoutRef.current);
    setShowCollectionsDropdown(true);
  };

  const handleCollectionMouseLeave = () => {
    collectionTimeoutRef.current = setTimeout(() => {
      if (!collectionDropdownRef.current?.matches(':hover')) {
        setShowCollectionsDropdown(false);
      }
    }, 300);
  };

  const handleDropdownMouseEnter = () => {
    if (categoryTimeoutRef.current) clearTimeout(categoryTimeoutRef.current);
    if (collectionTimeoutRef.current) clearTimeout(collectionTimeoutRef.current);
  };

  const handleDropdownMouseLeave = () => {
    setShowCategoriesDropdown(false);
    setShowCollectionsDropdown(false);
    setHoveredCategory(null);
  };

  const isCategoriesActive =
    location.pathname.startsWith('/category') || location.pathname.startsWith('/subcategory');

  const isCollectionsActive = location.pathname.startsWith('/collection');

  const renderNavLinks = () => (
    <>
      <Nav.Link as={NavLink} to="/home" className={({ isActive }) => (isActive ? 'active' : '')}>
        Home
      </Nav.Link>
      <Nav.Link
        as={NavLink}
        to="/product-list"
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Shop
      </Nav.Link>
      {categories.length > 0 && (
        <div
          className={`nav-item dropdown ${isCategoriesActive ? 'active' : ''}`}
          onMouseEnter={handleCategoryMouseEnter}
          onMouseLeave={handleCategoryMouseLeave}
        >
          <Nav.Link className="nav-link d-flex align-items-center">
            Categories <i className="fa-solid fa-chevron-down ml-1"></i>
          </Nav.Link>
          {showCategoriesDropdown && (
            <div 
              className="dropdown-menu show"
              ref={categoryDropdownRef}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              {categories.map((category) => (
                <div
                  key={category.CategoryId}
                  className="dropdown-submenu"
                  onMouseEnter={() => setHoveredCategory(category.CategoryId)}
                >
                  <div 
                    className="dropdown-item" 
                    onClick={() => handleCategoryClick(category.Slug)}
                  >
                    {category.Category}
                  </div>
                  {hoveredCategory === category.CategoryId && category.subCategories.length > 0 && (
                    <div 
                      className="dropdown-menu show"
                      onMouseEnter={handleDropdownMouseEnter}
                      onMouseLeave={handleDropdownMouseLeave}
                    >
                      {category.subCategories.map((subCategory) => (
                        <div
                          key={subCategory.SubCategoryId}
                          className="dropdown-item"
                          onClick={() => handleSubCategoryClick(subCategory.Slug)}
                        >
                          {subCategory.SubCategory}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {collections.length > 0 && (
        <div
          className={`nav-item dropdown ${isCollectionsActive ? 'active' : ''}`}
          onMouseEnter={handleCollectionMouseEnter}
          onMouseLeave={handleCollectionMouseLeave}
        >
          <Nav.Link className="nav-link d-flex align-items-center">
            Collections <i className="fa-solid fa-chevron-down ml-1"></i>
          </Nav.Link>
          {showCollectionsDropdown && (
            <div 
              className="dropdown-menu show"
              ref={collectionDropdownRef}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              {collections.map((collection) => (
                <div
                  key={collection.CollectionId}
                  className="dropdown-item"
                  onClick={() => handleCollectionClick(collection.Slug)}
                >
                  {collection.Collection}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <Nav.Link
        as={NavLink}
        to="/about-us"
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        About
      </Nav.Link>
      <Nav.Link
        as={NavLink}
        to="/contact-us"
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Contact Us
      </Nav.Link>
      <Nav.Link
        as={NavLink}
        to="/sale"
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Sale
      </Nav.Link>
      {/* <Nav.Link
        as={NavLink}
        to="/testimonials"
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Testimonials
      </Nav.Link> */}
    </>
  );

  const renderOffcanvasLinks = () => (
    <>
      <Nav.Link
        as={NavLink}
        to="/home"
        onClick={handleOffcanvasClose}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Home
      </Nav.Link>
      <Nav.Link
        as={NavLink}
        to="/product-list"
        onClick={handleOffcanvasClose}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Shop
      </Nav.Link>
      {categories.length > 0 && (
        <>
          <Nav.Link onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}>
            Categories{' '}
            <i
              className={`fa-solid fa-chevron-${
                showCategoriesDropdown ? 'up' : 'down'
              } ml-1`}
            ></i>
          </Nav.Link>
          {showCategoriesDropdown &&
            categories.map((category) => (
              <React.Fragment key={category.CategoryId}>
                <Nav.Link className="pl-3" onClick={() => handleCategoryClick(category.Slug)}>
                  {category.Category}
                </Nav.Link>
                {category.subCategories.map((subCategory) => (
                  <Nav.Link
                    key={subCategory.SubCategoryId}
                    className="pl-5"
                    onClick={() => handleSubCategoryClick(subCategory.Slug)}
                  >
                    {subCategory.SubCategory}
                  </Nav.Link>
                ))}
              </React.Fragment>
            ))}
        </>
      )}
      {collections.length > 0 && (
        <>
          <Nav.Link onClick={() => setShowCollectionsDropdown(!showCollectionsDropdown)}>
            Collections{' '}
            <i
              className={`fa-solid fa-chevron-${
                showCollectionsDropdown ? 'up' : 'down'
              } ml-1`}
            ></i>
          </Nav.Link>
          {showCollectionsDropdown &&
            collections.map((collection) => (
              <Nav.Link
                key={collection.CollectionId}
                className="pl-3"
                onClick={() => handleCollectionClick(collection.Slug)}
              >
                {collection.Collection}
              </Nav.Link>
            ))}
        </>
      )}
      <Nav.Link
        as={NavLink}
        to="/about-us"
        onClick={handleOffcanvasClose}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        About
      </Nav.Link>
      <Nav.Link
        as={NavLink}
        to="/contact-us"
        onClick={handleOffcanvasClose}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Contact Us
      </Nav.Link>
      <Nav.Link
        as={NavLink}
        to="/sale"
        onClick={handleOffcanvasClose}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Sale
      </Nav.Link>
      {/* <Nav.Link
        as={NavLink}
        to="/testimonials"
        onClick={handleOffcanvasClose}
        className={({ isActive }) => (isActive ? 'active' : '')}
      >
        Testimonials
      </Nav.Link> */}
    </>
  );

  return (
    <>
      <Navbar variant="dark" expand="xl" className="navbar">
        <Container className="d-flex align-items-center justify-content-between custom-container">
          <Navbar.Brand as={NavLink} to="/home" className="navbar-brand">
            <img src={Logo} className="logo" alt="Logo" />
          </Navbar.Brand>
          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            onClick={handleOffcanvasShow}
            className="d-xl-none"
          />
          <Navbar.Collapse
            id="basic-navbar-nav"
            className="justify-content-center d-none d-xl-flex"
          >
            <Nav className="nav-center">{renderNavLinks()}</Nav>
          </Navbar.Collapse>
          <div className="nav-icons ml-auto">
            <Link to="#" onClick={handleSearchModalOpen}>
              <i className="fa-solid fa-magnifying-glass"></i>
            </Link>
            {/* Updated profile icon with auth check */}
            <Link to="#" onClick={handleProfileClick}>
              <i className="fa-regular fa-user"></i>
            </Link>
            <Link to="/cart" className="icon-container">
              {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
              <i className="fa-solid fa-cart-shopping"></i>
            </Link>
            <Link to="/wishlist" className="icon-container">
              {wishlistCount > 0 && (
                <span className="wishlist-count">{wishlistCount}</span>
              )}
              <i className="fa-regular fa-heart"></i>
            </Link>
          </div>
        </Container>
      </Navbar>

      <Offcanvas
        show={showOffcanvas}
        onHide={handleOffcanvasClose}
        placement="end"
        className="d-xl-none"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column">{renderOffcanvasLinks()}</Nav>
        </Offcanvas.Body>
      </Offcanvas>

      <SearchModal show={showSearchModal} handleClose={handleSearchModalClose} />
    </>
  );
};

export default NavbarComponent;